/**
 * Keeps reviewer-preview tokens out of Sentry.
 *
 * A preview link is a bearer credential: `/blog/preview/<token>` shows an
 * unpublished article, and `/projects/preview/<token>` an unpublished project,
 * to whoever holds it. Sentry is a third party, and the
 * SDK records URLs and route params everywhere: the request, the transaction
 * name, breadcrumbs for every navigation and fetch, span names and data (the
 * TanStack Router integration writes each route param as
 * `url.path.params.<key>`, `url.path.parameter.<key>` and `params.<key>`; a
 * web-vital span copies the route name into a `transaction` attribute), the
 * Referer header, error messages. `scrubPreviewTokens` goes through a whole
 * payload and rewrites the token out of every string in it.
 *
 * A payload leaves through one of four hooks, and each needs the scrubber:
 * `beforeSend` (errors), `beforeSendTransaction` (transactions),
 * `beforeBreadcrumb` (breadcrumbs, before they join any event) and
 * `beforeSendSpan` (every span, including the standalone ones such as INP,
 * which are sent on their own and pass none of the other three). Spread
 * `previewScrubbingHooks` into every `Sentry.init` so none is forgotten.
 *
 * What is rewritten:
 * - `/blog/preview/<token>` and `/projects/preview/<token>`, with or without
 *   the `/lv` prefix and with the slashes percent-encoded once or twice,
 *   become `/blog/preview/[token]` and `/projects/preview/[token]`.
 * - The `payload` query parameter, which is where a GET server function sends
 *   its input (`/_serverFn/<id>?payload=...`), becomes `payload=[redacted]`.
 *   The preview's server function carries the token there. Its id is a hash in
 *   production, so the preview call cannot be told apart from any other, and
 *   every server function's payload is redacted instead.
 * - Anything shaped like a token (base64url body, a dot, a 43-character
 *   base64url HMAC-SHA256), wherever it stands, becomes `[token]`.
 * - The value of any key whose name ends in `token` (`params.token`,
 *   `previewToken`, ...) becomes `[token]`, whatever it looks like.
 * - The message, stack and cause of an Error, which are not plain data.
 *
 * The result is a copy: plain objects, arrays and Errors are rebuilt, and the
 * input is never written to. A breadcrumb holds live values (the very
 * arguments array a `console.*` call is about to print, the app's own Error),
 * and those must reach the console and the app exactly as they were.
 */

// The token is base64url text and a dot; `$` and brackets let the route
// template (`$token`) and our own placeholder through unchanged. Anything else
// (a comma in a baggage header, a quote, `&`, `?`, `#`, `/`, a space) ends it,
// as does an encoded slash, question mark, hash, ampersand or percent sign.
const PREVIEW_PATH_RE =
  /(?:(?:\/|%2F|%252F)lv)?(?:\/|%2F|%252F)(blog|projects)(?:\/|%2F|%252F)preview(?:\/|%2F|%252F)(?:[\w.~$[\]-]|%(?!2f|3f|23|26|25))+/gi;
const PAYLOAD_PARAM_RE = /(^|[?&])payload=[^&#\s"']*/g;
// `signPreviewToken`'s output: base64url JSON (`{"exp":…,"id":"<uuid>"}`, 83
// characters), a dot, a base64url SHA-256 HMAC (always 43 characters). The
// body is bounded so a long run of word characters is scanned in linear time;
// an unbounded `{20,}` backtracks quadratically (100 KB took 8 s).
const BARE_TOKEN_RE = /[\w-]{20,256}\.[\w-]{43}(?![\w-])/g;
const TOKEN_KEY_RE = /token$/i;

export const PREVIEW_PATH_REPLACEMENT = "/blog/preview/[token]";
const previewPathReplacement = (_match: string, section: string) =>
  `/${section.toLowerCase()}/preview/[token]`;
export const PAYLOAD_REPLACEMENT = "payload=[redacted]";
export const TOKEN_REPLACEMENT = "[token]";

type Copies = Map<object, unknown>;

function scrubString(value: string): string {
  return value
    .replace(PREVIEW_PATH_RE, previewPathReplacement)
    .replace(PAYLOAD_PARAM_RE, `$1${PAYLOAD_REPLACEMENT}`)
    .replace(BARE_TOKEN_RE, TOKEN_REPLACEMENT);
}

/** A property's scrubbed value; any string under a token-named key goes. */
function scrubProperty(key: string, value: unknown, copies: Copies): unknown {
  return TOKEN_KEY_RE.test(key) && typeof value === "string"
    ? TOKEN_REPLACEMENT
    : scrubValue(value, copies);
}

/**
 * A copy of an Error with every own property scrubbed, message, stack and
 * cause included (they are not enumerable, so the SDK's own serialisation, or
 * a later reader, would otherwise carry them out untouched). The prototype is
 * kept, so it is still an Error of the same kind.
 */
function copyError(error: Error, copies: Copies): Error {
  const copy = Object.create(Object.getPrototypeOf(error)) as Error;
  copies.set(error, copy);
  for (const key of Object.getOwnPropertyNames(error)) {
    const descriptor = Object.getOwnPropertyDescriptor(error, key);
    if (descriptor && "value" in descriptor) {
      Object.defineProperty(copy, key, {
        ...descriptor,
        value: scrubProperty(key, descriptor.value, copies),
      });
    }
  }
  // Some engines keep `stack` as an accessor on the prototype.
  if (!Object.hasOwn(copy, "stack") && typeof error.stack === "string") {
    Object.defineProperty(copy, "stack", {
      configurable: true,
      value: scrubString(error.stack),
      writable: true,
    });
  }
  return copy;
}

function copyArray(array: unknown[], copies: Copies): unknown[] {
  const copy: unknown[] = [];
  copies.set(array, copy);
  for (const item of array) {
    copy.push(scrubValue(item, copies));
  }
  return copy;
}

function copyRecord(
  record: Record<string, unknown>,
  prototype: object | null,
  copies: Copies
): Record<string, unknown> {
  const copy = Object.create(prototype) as Record<string, unknown>;
  copies.set(record, copy);
  for (const key of Object.keys(record)) {
    copy[key] = scrubProperty(key, record[key], copies);
  }
  return copy;
}

function scrubValue(value: unknown, copies: Copies): unknown {
  if (typeof value === "string") {
    return scrubString(value);
  }
  if (value === null || typeof value !== "object") {
    return value;
  }
  if (copies.has(value)) {
    return copies.get(value);
  }
  if (Array.isArray(value)) {
    return copyArray(value, copies);
  }
  if (value instanceof Error) {
    return copyError(value, copies);
  }
  // Payloads are plain data. Anything else (a Date, a Map, a class instance)
  // is passed on as it is rather than half-rewritten; the SDK does not
  // serialise their contents.
  const prototype = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) {
    return value;
  }
  return copyRecord(value as Record<string, unknown>, prototype, copies);
}

/**
 * A copy of `value` with preview tokens rewritten out of every string in it;
 * `value` itself is left as it was. Suits each of Sentry's `beforeSend`,
 * `beforeSendTransaction`, `beforeBreadcrumb` and `beforeSendSpan` as it is.
 */
export function scrubPreviewTokens<T>(value: T): T {
  return scrubValue(value, new Map()) as T;
}

/** The four hooks every payload leaves through, each scrubbing. Spread into every init. */
export const previewScrubbingHooks = {
  beforeBreadcrumb: scrubPreviewTokens,
  beforeSend: scrubPreviewTokens,
  beforeSendSpan: scrubPreviewTokens,
  beforeSendTransaction: scrubPreviewTokens,
};
